/**
 * Meta'nın kendi optimizasyon önerileri — Opportunity Score (Fırsat Puanı) + öneri listesi.
 *
 * Kaynak: backend /meta/{accountId}/opportunity (Marketing API: act_{id}?fields=opportunity_score
 * ve act_{id}/recommendations, locale=tr_TR). Veri hesap başına bir kez çekilip modül
 * önbelleğinde paylaşılır; Genel Bakış kartı, kampanya satırı rozetleri ve Optimizasyon
 * Önerileri sekmesi aynı isteği kullanır.
 *
 * Öneriler salt okunur gösterilir: "Ads Manager'da Uygula" Meta'nın döndürdüğü derin
 * bağlantıyı açar. Bu özellik Meta Marketing API'nin öneri motorudur, Muse değildir.
 */
import React from 'react';
import axios from 'axios';
import { T, tint } from './poyais-ui/theme';
import { Icon } from './poyais-ui/icons';
import { useIsMobile } from './poyais-ui/hooks';
import { useLang, addTranslations } from './i18n';

addTranslations({
  'Meta Fırsat Puanı': 'Meta Opportunity Score', "Meta'nın Önerileri": "Meta's Recommendations",
  'Meta Öneriyor': 'Meta Recommends',
  'Meta şu an çok sık istek aldığı için önerileri gösteremiyor. Birkaç dakika sonra tekrar deneyin.': 'Meta is receiving too many requests right now and can’t show recommendations. Try again in a few minutes.',
  'Meta önerileri şu an alınamadı.': 'Meta recommendations are unavailable right now.', 'potansiyel': 'potential',
  "Ads Manager'da Uygula": 'Apply in Ads Manager', 'puan': 'points', 'öneri': 'recommendations',
  'Tümünü Göster': 'Show All', 'Daha Az Göster': 'Show Less', 'Hesap geneli': 'Account-wide',
  'Kampanya': 'Campaign', 'Reklam seti': 'Ad set', 'Reklam': 'Ad', 'Güncelle': 'Refresh',
  'Meta şu an bu hesap için öneri sunmuyor.': 'Meta has no recommendations for this account right now.',
  'Puan, hesabın Meta\'nın önerdiği en iyi uygulamalara ne kadar uyduğunu gösterir. Öneriyi uyguladığınızda yanındaki kadar puan artar.':
    'The score shows how closely the account follows Meta\'s best practices. Applying a recommendation raises it by the amount shown.',
  'Kreatif': 'Creative', 'Bütçe': 'Budget', 'Teklif': 'Bidding', 'Yerleşim': 'Placement', 'Katalog': 'Catalog',
  'Hedef kitle': 'Audience', 'Ölçümleme': 'Tracking', 'Hesap yapısı': 'Structure', 'Diğer': 'Other',
});

const API = '/api/v1';
/** Büyük harf, panelin diline göre: Türkçe'de i→İ, İngilizce'de i→I ("OPPORTUNITY"). */
const upperUi = (s: string) => {
  let en = false;
  try { en = localStorage.getItem('poyais_lang') === 'en'; } catch { }
  return en ? s.toUpperCase() : s.toLocaleUpperCase('tr');
};

const CAT: Record<string, { label: string; color: string }> = {
  creative: { label: 'Kreatif', color: T.purple }, budget: { label: 'Bütçe', color: T.green },
  bidding: { label: 'Teklif', color: T.blue }, placement: { label: 'Yerleşim', color: T.amber },
  catalog: { label: 'Katalog', color: T.accent }, audience: { label: 'Hedef kitle', color: T.blue },
  tracking: { label: 'Ölçümleme', color: T.red }, structure: { label: 'Hesap yapısı', color: T.amber },
  other: { label: 'Diğer', color: T.textDim },
};
const LEVEL: Record<string, string> = { campaign: 'Kampanya', adset: 'Reklam seti', ad: 'Reklam', adaccount: 'Hesap geneli' };

// ── Paylaşılan veri ──────────────────────────────────────────────────────────
const cache = new Map<string, Promise<any>>();
function load(accountId: string, lang: string, fresh = false) {
  const key = `${accountId}|${lang}`;
  if (fresh || !cache.has(key)) {
    const p = axios.get(`${API}/meta/${accountId}/opportunity`, { params: { lang, ...(fresh ? { fresh: true } : {}) } }).then(r => r.data);
    p.catch(() => cache.delete(key));
    cache.set(key, p);
  }
  return cache.get(key)!;
}
export function useMetaOpportunity(accountId?: string) {
  const { lang } = useLang();
  const [state, setState] = React.useState<{ data: any; loading: boolean; error: string | null }>({ data: null, loading: !!accountId, error: null });
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    if (!accountId) { setState({ data: null, loading: false, error: null }); return; }
    let alive = true;
    setState(s => ({ ...s, loading: true, error: null }));
    load(accountId, lang, tick > 0)
      .then(d => { if (alive) setState({ data: d, loading: false, error: null }); })
      .catch(e => { if (alive) setState({ data: null, loading: false, error: e?.response?.data?.detail || 'Meta önerileri alınamadı' }); });
    return () => { alive = false; };
  }, [accountId, lang, tick]);
  return { ...state, refresh: () => setTick(x => x + 1) };
}

// ── Puan halkası ─────────────────────────────────────────────────────────────
function ScoreRing({ score, size = 96 }: { score: number; size?: number }) {
  const r = size / 2 - 7, c = 2 * Math.PI * r, pct = Math.max(0, Math.min(100, score)) / 100;
  const color = score >= 80 ? T.green : score >= 60 ? T.amber : T.red;
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--py-track-bg)" strokeWidth={8} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={8} strokeLinecap="round"
        strokeDasharray={`${c * pct} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" style={{ fontSize: size * 0.3, fontWeight: 800, fill: 'var(--py-text)' }}>{score}</text>
    </svg>
  );
}

function RecRow({ r }: { r: any }) {
  const { t } = useLang();
  const [open, setOpen] = React.useState(false);
  const cat = CAT[r.category] || CAT.other;
  const objs: any[] = r.objects || [];
  const named = objs.filter(o => o.level !== 'adaccount');
  return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.surface, overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: '100%', textAlign: 'left' as const, display: 'flex', gap: 12, alignItems: 'flex-start',
        padding: '12px 14px', border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
        <span style={{ fontSize: 10.5, fontWeight: 800, color: cat.color, background: tint(cat.color, 12), borderRadius: 999, padding: '3px 9px', flexShrink: 0, marginTop: 1 }}>{t(cat.label)}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: T.text, lineHeight: 1.45 }}>{dot(r.title)}</span>
          <span style={{ display: 'block', fontSize: 11.5, color: T.textDim, marginTop: 3 }}>
            {named.length ? named.slice(0, 2).map(o => `${t(LEVEL[o.level] || '')}: ${o.name || o.id}`).join(' · ') + (named.length > 2 ? ` +${named.length - 2}` : '') : t('Hesap geneli')}
          </span>
        </span>
        {r.score_lift > 0 && <span style={{ fontSize: 12, fontWeight: 800, color: T.greenText, flexShrink: 0 }}>+{r.score_lift} {t('puan')}</span>}
        <span style={{ color: T.textDim, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s', display: 'flex', flexShrink: 0 }}><Icon.chevron size={15} /></span>
      </button>
      {open && (
        <div style={{ padding: '0 14px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {r.body && <div style={{ fontSize: 12.5, color: T.textMid, lineHeight: 1.6 }}>{dot(r.body)}</div>}
          {named.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {named.map(o => (
                <div key={o.id} style={{ fontSize: 12, color: T.text, display: 'flex', gap: 8 }}>
                  <span style={{ color: T.textDim, minWidth: 80 }}>{t(LEVEL[o.level] || '')}</span>
                  <span style={{ fontWeight: 600 }}>{o.name || o.id}</span>
                  {o.level !== 'campaign' && o.campaign_name && <span style={{ color: T.textDim }}>· {o.campaign_name}</span>}
                </div>
              ))}
            </div>
          )}
          {r.url && (
            <a href={r.url} target="_blank" rel="noreferrer" style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700,
              color: '#fff', background: T.blue, borderRadius: 9, padding: '7px 13px', textDecoration: 'none' }}>
              {t("Ads Manager'da Uygula")} <Icon.external size={12} />
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// Meta'nın öneri metinleri noktasız gelebiliyor; cümle sonuna nokta konur.
const dot = (s?: string) => { const x = (s || '').trim(); return x && !/[.!?…:)]$/.test(x) ? x + '.' : x; };

// ── Kart: Meta Genel Bakış + Optimizasyon Önerileri ─────────────────────────
export function MetaOpportunityCard({ accountId, initial = 3 }: { accountId?: string; initial?: number }) {
  const { t } = useLang();
  const isMobile = useIsMobile();
  const { data, loading, error, refresh } = useMetaOpportunity(accountId);
  const [all, setAll] = React.useState(false);
  if (!accountId) return null;
  if (loading && !data) return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 16, background: T.surface, padding: 20, fontSize: 12.5, color: T.textDim }}>{t('Meta Fırsat Puanı')}…</div>
  );
  if (error) return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 16, background: T.surface, padding: 16, fontSize: 12, color: T.textDim }}>
      <b style={{ color: T.text }}>{t('Meta Fırsat Puanı')}</b> · {/limit/i.test(error)
        ? t('Meta şu an çok sık istek aldığı için önerileri gösteremiyor. Birkaç dakika sonra tekrar deneyin.')
        : t('Meta önerileri şu an alınamadı.')}
    </div>
  );
  const recs: any[] = data?.recommendations || [];
  const score = Number(data?.opportunity_score ?? 0);
  const potential = Math.min(100 - score, recs.reduce((s, r) => s + (r.score_lift || 0), 0));
  const shown = all ? recs : recs.slice(0, initial);
  return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 16, background: T.surface, overflow: 'hidden' }}>
      <div style={{ padding: isMobile ? 16 : '18px 20px', display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' as const,
        background: `linear-gradient(135deg, ${tint(T.blue, 7)}, var(--py-surface) 60%)`, borderBottom: recs.length ? `1px solid ${T.border}` : 'none' }}>
        {data?.opportunity_score !== null && data?.opportunity_score !== undefined && <ScoreRing score={score} size={isMobile ? 76 : 90} />}
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em', color: T.blueText }}>{upperUi(t('Meta Fırsat Puanı'))}</div>
          <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 800, color: T.text, margin: '4px 0' }}>
            {recs.length ? `${recs.length} ${t('öneri')}${potential > 0 ? ` · +${potential} ${t('puan')} ${t('potansiyel')}` : ''}` : t('Meta şu an bu hesap için öneri sunmuyor.')}
          </div>
          <div style={{ fontSize: 11.5, color: T.textDim, lineHeight: 1.5 }}>
            {t('Puan, hesabın Meta\'nın önerdiği en iyi uygulamalara ne kadar uyduğunu gösterir. Öneriyi uyguladığınızda yanındaki kadar puan artar.')}
          </div>
        </div>
        <button onClick={refresh} title={t('Güncelle')} style={{ background: 'none', border: `1px solid ${T.border}`, borderRadius: 8, cursor: 'pointer', color: T.textMid, padding: '5px 9px', fontFamily: 'inherit', fontSize: 12 }}>↻</button>
      </div>
      {recs.length > 0 && (
        <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {shown.map((r, i) => <RecRow key={`${r.type}-${i}`} r={r} />)}
          {recs.length > initial && (
            <button onClick={() => setAll(a => !a)} style={{ alignSelf: 'center', background: 'none', border: 'none', cursor: 'pointer', color: T.accent, fontSize: 12, fontWeight: 700, fontFamily: 'inherit', padding: 4 }}>
              {all ? t('Daha Az Göster') : `${t('Tümünü Göster')} (${recs.length})`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Rozet: kampanya satırı / bizim öneri kartımız ────────────────────────────
export function MetaRecBadge({ accountId, campaignId, label }: { accountId?: string; campaignId?: string; label?: string }) {
  const { t } = useLang();
  const { data } = useMetaOpportunity(accountId);
  if (!campaignId || !data) return null;
  const hits = (data.recommendations || []).filter((r: any) => (r.campaign_ids || []).includes(String(campaignId)));
  if (!hits.length) return null;
  return (
    <span title={hits.map((r: any) => dot(r.title)).join('\n')} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 8, fontSize: 10, fontWeight: 800,
      color: T.blueText, background: tint(T.blue, 12), border: `1px solid ${tint(T.blue, 40)}`, borderRadius: 999, padding: '1px 7px', whiteSpace: 'nowrap' as const, verticalAlign: 'middle' }}>
      {label || t('Meta Öneriyor')}{hits.length > 1 ? ` · ${hits.length}` : ''}
    </span>
  );
}
